import { Factory } from "@/data/dao/Factory";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import { FulfillmentPositionDisplayMode, FulfillmentProductionState } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IOperatorFulfillment } from "@/data/model/Operator";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { Logging } from "@/data/util/Logging";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";
import { createVueComponent } from "@/shared/utils/vue";

import { Popup } from "@/components/dialog/Popup";

import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
import FulfillmentInfoHeader from "@/operator/components/fulfillment/FulfillmentInfoHeader.vue";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { FulfillmentHelper } from "@/operator/data/FulfillmentHelper";
import { FulfillmentQueueIndicator } from "@/operator/elements/FulfillmentQueueIndicator";
import { OrderItemsList } from "@/operator/elements/OrderItemList";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";

import { OperatorBaseModal } from "./OperatorBaseModal";

export class FulfillmentModal extends OperatorBaseModal {
    private $head: JQuery;
    private $summaryHeader: JQuery;

    private queueIndicator: FulfillmentQueueIndicator;

    private $itemList: JQuery;

    private buttonTimes = [1, 3, 10, 20];
    private $timeSkips: JQuery | undefined = undefined;
    private $decreaseBtns: JQuery | undefined = undefined;
    private $increaseBtns: JQuery | undefined = undefined;
    private $resolveBtn: JQuery;
    private $setReadyBtn: JQuery;

    private isSaving?: boolean;
    public fulfillment?: IOperatorFulfillment;

    private operatorDao: OperatorDao;
    private context: ProductionContext;

    private productionState?: FulfillmentProductionState;
    private fulfillmentInfoHeaderComponent: any;

    constructor() {
        super();

        this.operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
        this.context = ProductionContext.getInstance();

        this.$element.addClass("fulfillment");

        this.$body.addClass("scrollable");
        this.$head = $('<div class="head" />').appendTo(this.$body);
        this.$summaryHeader = $('<div class="order-summary-header" />').appendTo(this.$head);

        this.queueIndicator = new FulfillmentQueueIndicator();
        this.queueIndicator.renderTo(this.$head);

        this.$itemList = $('<div class="itemlist" />').appendTo(this.$body);

        const buttons = $('<div class="buttons" />').appendTo(this.$body);

        this.initTimeSkipsComponent(buttons);

        const resolves = $('<div class="resolve" />').appendTo(buttons);

        this.$resolveBtn = Ripple.bind(
            $(
                '<button class="btn"><i class="fa-light fa-person-carry-box"></i><i class="fal fa-check"></i></button>'
            ).click((e) => {
                if (this.isSaving || this.fulfillment === undefined) {
                    e.stopPropagation();
                    return;
                }

                this.isSaving = true;
                Logging.LogApiCall("Sending request: 'SetFulfillmentFulfilled'");
                this.operatorDao
                    .setFulfillmentFulfilled(this.fulfillment.id, {
                        orderId: this.fulfillment.orderId,
                    })
                    .then(() => {
                        Logging.LogApiCall("Request: 'SetFulfillmentFulfilled' sent");
                        this.isSaving = false;
                        this.close();
                    })
                    .catch((ex) => {
                        logError(ex);
                        this.isSaving = false;
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.general.operationFailed,
                            uniqueId: "operation-failed-popup",
                        });
                    });
            })
        ).appendTo(resolves);

        this.$setReadyBtn = Ripple.bind(
            $('<button class="btn"><i class="fal fa-cogs"></i><i class="fal fa-check"></i></button>').click((e) => {
                if (this.isSaving || this.fulfillment === undefined) {
                    e.stopPropagation();
                    return;
                }

                this.isSaving = true;
                Logging.LogApiCall("Sending request: 'SetFulfillmentReady'");
                this.operatorDao
                    .setFulfillmentReady(this.fulfillment.id, {
                        orderId: this.fulfillment.orderId,
                    })
                    .then(() => {
                        Logging.LogApiCall("Request: 'SetFulfillmentReady' sent");
                        this.isSaving = false;
                    })
                    .catch((ex) => {
                        logError(ex);
                        this.isSaving = false;
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.general.operationFailed,
                            uniqueId: "operation-failed-popup",
                        });
                    });
            })
        ).appendTo(resolves);
    }

    public setFulfillment(fulfillment: IOperatorFulfillment, group?: IFulfillmentGroup): FulfillmentModal {
        this.fulfillment = fulfillment;

        this.initFulfillmentInfoHeaderComponent();

        let groupName = trans.production.emptyFulfillmentGroup;
        if (group !== undefined) {
            groupName = getNameInLocationLanguage(group, true);
        }

        if (this.isPositionDisplay()) {
            this.$timeSkips?.hide();
        } else {
            this.$timeSkips?.show();
        }

        // first clear items list
        this.$itemList.empty();
        this.$itemList.append(`<div class="groupname">${Format.htmlEscape(groupName)}</div>`);

        OrderItemsList.render(this.$itemList, fulfillment.items, true);

        this.updateTime(new Date());

        return this;
    }

    private initTimeSkipsComponent(container: JQuery) {
        this.$timeSkips = $('<div class="timeskips" />').appendTo(container);

        const decrease = $('<div class="decrease" />').appendTo(this.$timeSkips);
        const increase = $('<div class="increase" />').appendTo(this.$timeSkips);

        for (const t of this.buttonTimes) {
            const decT = t * -1;

            Ripple.bindRippleOut(
                $(`<button class="grey btn" data-minutes="${decT}" data-positions="${decT}">${decT}m</button>`).click(
                    (e) => {
                        if (this.isSaving) {
                            e.stopPropagation();
                            return;
                        }

                        if (this.isPositionDisplay()) {
                            this.changeFulfillmentPosition(parseInt($(e.currentTarget).data("positions")));
                        } else {
                            this.setTimeOfFulfillment(parseInt($(e.currentTarget).data("minutes")));
                        }
                    }
                )
            ).appendTo(decrease);

            Ripple.bindRippleOut(
                $(`<button class="orange btn" data-minutes="${t}" data-positions="${t}">${t}m</button>`).click((e) => {
                    if (this.isSaving) {
                        e.stopPropagation();
                        return;
                    }

                    if (this.isPositionDisplay()) {
                        this.changeFulfillmentPosition(parseInt($(e.currentTarget).data("positions")));
                    } else {
                        this.setTimeOfFulfillment(parseInt($(e.currentTarget).data("minutes")));
                    }
                })
            ).appendTo(increase);
        }

        this.$decreaseBtns = decrease.find("> .btn");
        this.$increaseBtns = increase.find("> .btn");
    }

    private initFulfillmentInfoHeaderComponent() {
        if (this.fulfillmentInfoHeaderComponent) {
            this.fulfillmentInfoHeaderComponent.unmount();
        }
        this.$summaryHeader.empty();

        const props = {
            fulfillment: this.fulfillment,
        };
        const result = createVueComponent(FulfillmentInfoHeader, props, this.$summaryHeader[0]);
        this.fulfillmentInfoHeaderComponent = result.componentApp;
    }

    protected override onVisibilityChanged(isOpen: boolean) {
        if (!isOpen) {
            this.fulfillment = undefined;
            this.isSaving = false;
            operatorEvents.modals.closeAllModals.remove(this.close, this);
        } else {
            operatorEvents.modals.closeAllModals.add(this.close, this);
        }
    }

    private setTimeOfFulfillment(minutes: number) {
        if (this.fulfillment === undefined) return;

        this.isSaving = true;
        Logging.LogApiCall("Sending request: 'UpdateFulfillmentTime'");
        this.operatorDao
            .updateFulfillmentTime(this.fulfillment.id, {
                isRelative: this.isInPreparation(),
                orderId: this.fulfillment.orderId,
                preparationTime: DateTime.secondsToTimespan(minutes * 60),
            })
            .then(() => {
                Logging.LogApiCall("Request: 'UpdateFulfillmentTime' sent");
                this.isSaving = false;
            })
            .catch((ex) => {
                logError(ex);
                this.isSaving = false;

                Popup.show({
                    title: trans.general.failed,
                    message: trans.general.operationFailed,
                    uniqueId: "operation-failed-popup",
                });
            });
    }

    private changeFulfillmentPosition(positionChange: number) {
        if (this.fulfillment === undefined) return;

        this.isSaving = true;
        Logging.LogApiCall("Sending request: 'UpdateFulfillmentPosition'");
        this.operatorDao
            .updateFulfillmentPosition(this.fulfillment.id, {
                isRelative: this.isPositionDisplay() && this.isInPreparation(),
                positionChange: positionChange,
            })
            .then(() => {
                Logging.LogApiCall("Request: 'UpdateFulfillmentPosition' sent");
                this.isSaving = false;
            })
            .catch((ex) => {
                logError(ex);
                this.isSaving = false;

                Popup.show({
                    title: trans.general.failed,
                    message: trans.general.operationFailed,
                    uniqueId: "operation-failed-popup",
                });
            });
    }

    public updateTime(date: Date) {
        if (this.fulfillment === undefined) return;

        this.productionState = FulfillmentHelper.getFulfillmentProductionState(this.fulfillment);

        // update queue position or time
        this.queueIndicator.updateQueueIndicator(date, this.fulfillment);

        const timeDiff = FulfillmentHelper.getTimeLeftToReadyDate(this.fulfillment);
        const itemsInQueue = this.context.getFulfillmentGroupQueueLength(this.fulfillment.fulfillmentGroupId);

        this.$decreaseBtns?.each((i, e) => {
            const $btn = $(e);

            const suffix = this.isPositionDisplay() ? " &nbsp;" : " m"; //todo: nbsp because of styling issues
            const buttonValue = `-${this.buttonTimes[i]}${suffix}`;
            $btn.html(buttonValue);

            const minutes = parseInt($btn.data("minutes"));
            const positionsChange = parseInt($btn.data("positions"));

            const isBtnEnabled =
                (this.isInPreparation() &&
                    this.isDurationDisplay() &&
                    this.isTimeDecreaseAvailable(minutes, timeDiff)) ||
                (this.isInPreparation() &&
                    this.isPositionDisplay() &&
                    this.isPositionChangeAllowed(positionsChange, itemsInQueue));

            $btn.prop("disabled", !isBtnEnabled);
        });

        this.$increaseBtns?.each((i, e) => {
            const $btn = $(e);
            const prefix = this.isInPreparation() ? "+" : "&nbsp; "; //todo: nbsp because of styling issues
            const suffix = this.isPositionDisplay() ? "&nbsp;" : " m"; //todo: nbsp because of styling issues
            const buttonValue = `${prefix}${this.buttonTimes[i]}${suffix}`;
            $btn.html(buttonValue);

            const positionsChange = parseInt($btn.data("positions"));

            const isBtnEnabled =
                this.isDurationDisplay() ||
                (this.isPositionDisplay() &&
                    this.isInPreparation() &&
                    this.isPositionChangeAllowed(positionsChange, itemsInQueue)) ||
                (this.isPositionDisplay() && !this.isInPreparation() && positionsChange == 1);

            $btn.prop("disabled", !isBtnEnabled);
        });

        // 'Set Ready' button enabled:
        // 1.for Duration display - when Ready Date is in the future
        // 2.for Position display - when state is in InPreparation
        const isSetReadyBtnEnabled = this.isInPreparation();
        const isResolveBtnEnabled = this.isReady();

        this.$setReadyBtn.prop("disabled", !isSetReadyBtnEnabled);
        this.$resolveBtn.prop("disabled", !isResolveBtnEnabled);
    }

    private isReady() {
        return this.productionState === FulfillmentProductionState.Ready;
    }

    private isInPreparation() {
        return (
            this.productionState === FulfillmentProductionState.InPreparation ||
            this.productionState === FulfillmentProductionState.ReadyShortly
        );
    }

    private isPositionDisplay() {
        return this.fulfillment?.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position;
    }

    private isDurationDisplay() {
        return this.fulfillment?.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Duration;
    }

    // checks if it's possible to change position relatively to current position
    private isPositionChangeAllowed(positionChange: number, currentQueueSize: number) {
        const newPosition = (this.fulfillment!.currentQueuePosition ?? 0) + positionChange;
        // not possible to change position to less than 1
        if (newPosition < 1) return false;

        // not possible to change position to more than number of items in queue
        if (newPosition > currentQueueSize) return false;

        return true;
    }

    // validate if date will still be in the futer after button click - if it will then allow action
    private isTimeDecreaseAvailable(minutes: number, timeDiff: number) {
        return timeDiff + minutes * 60000 > 0;
    }

    protected override beforeResize(): void {}
}
